import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { CmdHotKeyIcon } from '@modules/common/components/HotKeyIcons/CmdHotKeyIcon';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGetArtefactScope, useHotkey } from '@modules/common/hooks';
import { DashboardDescriptionInput } from '@modules/dashboard/components/DashboardDescriptionInput';
import { DashboardNameAndEmojiInput } from '@modules/dashboard/components/DashboardNameAndEmojiInput';
import {
  DASHBOARD_DESCRIPTION_MAX_LENGTH,
  DASHBOARD_NAME_MAX_LENGTH,
} from '@modules/dashboard/constants';
import {
  getDashboardRoute,
  getEmojiFromDashboard,
  getShortMetadataForDashboard,
} from '@modules/dashboard/helpers';
import { useDuplicateDashboard } from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { KeyboardReturnRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const DuplicateDashboardDialogContent = () => {
  const navigate = useNavigate();

  const { closeModal } = useMuiModalContext();
  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const defaultName = `Copy of ${dashboard.title}`;

  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState(dashboard.description);
  const [emoji, setEmoji] = useState(getEmojiFromDashboard(dashboard));
  const [shouldDuplicateAnalyses, setShouldDuplicateAnalyses] = useState(true);

  const [dialogRefInState, setDialogRefInState] =
    useState<HTMLDivElement | null>(null);

  const [
    duplicateDashboardMutation,
    { loading: isDuplicateDashboardInProgress },
  ] = useDuplicateDashboard();

  const artefactScope = useGetArtefactScope();

  const isDisabled =
    !name.trim() ||
    name.length > DASHBOARD_NAME_MAX_LENGTH ||
    description.length > DASHBOARD_DESCRIPTION_MAX_LENGTH;

  const duplicateDashboard = async () => {
    if (isDisabled || isDuplicateDashboardInProgress) {
      return;
    }

    try {
      const resp = await duplicateDashboardMutation({
        dashboardID: dashboard.id,
        shouldDuplicateAnalyses,
        title: name,
        description,
        shortMetadata: getShortMetadataForDashboard({
          dashboardEmoji: emoji,
        }),
        scope: artefactScope,
      });

      const duplicatedDashboard = resp?.data?.duplicateDashboard?.dashboard;

      if (duplicatedDashboard) {
        closeModal();
        navigate(
          getDashboardRoute({
            title: duplicatedDashboard.title,
            viewToken: duplicatedDashboard.viewToken,
          }),
        );
        showSuccessNotification({
          contentComponent: 'Dashboard duplication was successful!',
          props: {},
        });
      }
    } catch {
      showErrorNotification({
        contentComponent:
          'Failed to duplicate the Dashboard. Please try again.',
        props: {},
      });
    }
  };

  useHotkey({
    hotkey: '$mod+enter',
    onHotkey: duplicateDashboard,
    isHotKeyDisabled: isDisabled,
  });

  return (
    <>
      <ModalHeader title="Duplicate Dashboard" />
      <Box
        component="form"
        ref={setDialogRefInState}
        onSubmit={(e) => {
          e.preventDefault();
          duplicateDashboard();
        }}
      >
        <Divider />
        <DialogContent>
          <Stack width="100%">
            <Stack spacing={2}>
              <DashboardNameAndEmojiInput
                emoji={emoji}
                onEmojiChange={setEmoji}
                name={name}
                onNameChange={setName}
                dialogRef={dialogRefInState}
              />
              <DashboardDescriptionInput
                description={description}
                onDescriptionChange={setDescription}
              />
            </Stack>
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldDuplicateAnalyses}
                    onChange={(_, checked) =>
                      setShouldDuplicateAnalyses(checked)
                    }
                  />
                }
                label="Duplicate all the Quantify and Feed in this Dashboard "
              />
              <Typography variant="caption" color="text.secondary" pl={4}>
                (Duplicating Quantify and Feed will create a copy editable by
                you.)
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Stack direction="row" m={1} spacing={2}>
            <Button
              onClick={() => closeModal()}
              variant="outlined"
              endIcon={<EscHotKeyIcon />}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              disabled={isDisabled}
              loading={isDuplicateDashboardInProgress}
              variant="contained"
              autoFocus
              endIcon={
                <CmdHotKeyIcon
                  plusHotKeyNode={<KeyboardReturnRounded />}
                  isDisabled={isDisabled}
                />
              }
            >
              Duplicate Dashboard
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Box>
    </>
  );
};
